/**
 * Utilitaires pour la gestion des domaines
 */

/**
 * Détermine l'état du renouvellement d'un domaine OVH
 * @param {Object} domainInfo - Informations du domaine
 * @returns {string} - 'automatic', 'manual', 'no_renew', ou 'unknown'
 */
export const getRenewalState = (domainInfo) => {
  if (!domainInfo) return 'unknown';
  
  // 1. renewalState (priorité absolue - données OVH directes)
  const renewalState = domainInfo.details?.renewalState;
  if (renewalState) {
    switch (renewalState) {
      case 'automatic_renew': return 'automatic';
      case 'manual_renew': return 'manual';
      case 'no_renew': return 'no_renew';
      default: break; // Continuer vers les autres vérifications
    }
  }
  
  // 2. renewalType (type de renouvellement OVH)
  const renewalType = domainInfo.details?.renewalType;
  if (renewalType) {
    if (renewalType.includes('automatic')) return 'automatic';
    if (renewalType.includes('manual')) return 'manual';
    if (renewalType.includes('no')) return 'no_renew';
  }
  
  // 3. autoRenew (fallback - champ simple, priorité sur renew.automatic)
  if (domainInfo.autoRenew === true) return 'automatic';
  if (domainInfo.autoRenew === false) return 'manual';
  
  // 4. renew.automatic (configuration de renouvellement)
  const renewAutomatic = domainInfo.renewal?.renew?.automatic;
  if (renewAutomatic === true) return 'automatic';
  if (renewAutomatic === false) return 'manual';
  
  // 5. renew.manualPayment (paiement manuel requis)
  const manualPayment = domainInfo.renewal?.renew?.manualPayment;
  if (manualPayment === true) return 'manual';
  
  // 6. Si aucun indicateur trouvé
  return 'unknown';
};

/**
 * Détermine le statut d'un domaine
 * @param {Object} domainInfo - Informations du domaine
 * @returns {string} - 'active', 'expiring', 'expired', 'pending', ou 'unknown'
 */
export const getDomainStatus = (domainInfo) => {
  if (!domainInfo) return 'unknown';
  
  const status = domainInfo.status;
  const expirationDate = domainInfo.expirationDate ? new Date(domainInfo.expirationDate) : null;
  const now = new Date();
  
  if (status === 'ok' || status === 'active') {
    if (expirationDate) {
      const daysUntilExpiration = Math.ceil((expirationDate - now) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiration < 0) return 'expired';
      if (daysUntilExpiration < 30) return 'expiring';
      return 'active';
    }
    return 'active';
  }
  
  if (status === 'expired') return 'expired';
  if (status === 'pending') return 'pending';
  
  return 'unknown';
};

/**
 * Calcule les jours restants avant expiration
 * @param {string} expirationDate - Date d'expiration (ISO string)
 * @returns {number|null} - Nombre de jours restants ou null
 */
export const getDaysUntilExpiration = (expirationDate) => {
  if (!expirationDate) return null;
  
  const now = new Date();
  const expDate = new Date(expirationDate);
  const days = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
  
  return days;
};

/**
 * Obtient la classe CSS pour l'état du renouvellement
 * @param {string} state - État du renouvellement
 * @returns {string} - Classe CSS Bootstrap
 */
export const getRenewalStateClass = (state) => {
  switch (state) {
    case 'automatic': return 'bg-success';
    case 'manual': return 'bg-warning';
    case 'no_renew': return 'bg-danger';
    default: return 'bg-secondary';
  }
};

/**
 * Obtient le texte pour l'état du renouvellement
 * @param {string} state - État du renouvellement
 * @returns {string} - Texte affiché
 */
export const getRenewalStateText = (state) => {
  switch (state) {
    case 'automatic': return 'Automatique';
    case 'manual': return 'Manuel';
    case 'no_renew': return 'Pas de renouvellement';
    default: return 'Inconnu';
  }
};

/**
 * Obtient la classe CSS pour le statut du domaine
 * @param {string} status - Statut du domaine
 * @returns {string} - Classe CSS Bootstrap
 */
export const getStatusClass = (status) => {
  switch (status) {
    case 'active': return 'bg-success';
    case 'expiring': return 'bg-warning';
    case 'expired': return 'bg-danger';
    case 'pending': return 'bg-info';
    default: return 'bg-secondary';
  }
};

/**
 * Obtient le texte pour le statut du domaine
 * @param {string} status - Statut du domaine
 * @returns {string} - Texte affiché
 */
export const getStatusText = (status) => {
  switch (status) {
    case 'active': return 'Actif';
    case 'expiring': return 'Expire bientôt';
    case 'expired': return 'Expiré';
    case 'pending': return 'En attente';
    default: return 'Inconnu';
  }
};

/**
 * Obtient la classe CSS pour les jours restants
 * @param {number|null} days - Nombre de jours restants
 * @returns {string} - Classe CSS Bootstrap
 */
export const getDaysRemainingClass = (days) => {
  if (days === null) return 'bg-secondary';
  if (days < 0) return 'bg-danger';
  if (days < 7) return 'bg-danger';
  if (days < 30) return 'bg-warning';
  if (days < 90) return 'bg-info';
  return 'bg-success';
};

/**
 * Obtient le texte pour les jours restants
 * @param {number|null} days - Nombre de jours restants
 * @returns {string} - Texte affiché
 */
export const getDaysRemainingText = (days) => {
  if (days === null) return 'N/A';
  if (days < 0) return 'Expiré';
  if (days === 0) return 'Expire aujourd\'hui';
  if (days === 1) return '1 jour';
  return `${days} jours`;
};
